import React, { useEffect, useState } from 'react';
import { Collapse, Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { marginLeft, right, left } from '@/utils/directions';
import {
  colorBackground, colorBorder, colorHover, colorText, colorTextAdditional, colorWhite,
} from '@/utils/palette';
import profileAvatar from '@/shared/img/profile/profile-placeholder.svg';
import { Button } from '@/shared/components/Button';
import {
  AUTH_SESSION_UPDATED_EVENT,
  clearAuthSession,
  getAuthSession,
  redirectToLogin,
  updateAuthSession,
} from '@/services/authService';
import { fetchAuthenticatedUser } from '@/services/usersService';
import { ModalFooterWrap } from '@/shared/components/ModalElements';
import TopbarMenuLink, { TopbarLink } from './TopbarMenuLink';
import { TopbarBack, TopbarDownIcon } from './BasicTopbarComponents';

const resolveUserImage = (usuario) => {
  const signedImage = typeof usuario?.imgAssinada === 'string' ? usuario.imgAssinada.trim() : '';
  if (signedImage) {
    return signedImage;
  }

  const defaultImage = typeof usuario?.img === 'string' ? usuario.img.trim() : '';
  return defaultImage;
};

const resolveUserDisplayName = (usuario) => {
  const nome = typeof usuario?.nome === 'string' ? usuario.nome.trim() : '';
  if (nome) {
    return nome;
  }

  const email = typeof usuario?.email === 'string' ? usuario.email.trim() : '';
  if (email) {
    return email;
  }

  return 'Portal VDC';
};

const TopbarProfile = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [session, setSession] = useState(() => getAuthSession());
  const [hasAvatarError, setHasAvatarError] = useState(false);

  useEffect(() => {
    const handleSessionUpdated = (event) => {
      setSession(event?.detail || getAuthSession());
    };

    const handleStorageChange = () => {
      setSession(getAuthSession());
    };

    window.addEventListener(AUTH_SESSION_UPDATED_EVENT, handleSessionUpdated);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, handleSessionUpdated);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const syncAuthenticatedUser = async () => {
      if (!getAuthSession()?.token) {
        return;
      }

      try {
        const response = await fetchAuthenticatedUser();
        const nextSession = updateAuthSession(currentSession => ({
          ...currentSession,
          usuario: {
            ...(currentSession?.usuario || {}),
            ...(response?.usuario || {}),
          },
        }));

        if (isMounted && nextSession) {
          setSession(nextSession);
        }
      } catch (_error) {
    // A tela segue com a sessão local se a sincronização falhar.
      }
    };

    syncAuthenticatedUser();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setHasAvatarError(false);
  }, [session?.usuario?.img, session?.usuario?.imgAssinada]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogoutClick = (event) => {
    event.preventDefault();
    setIsCollapsed(false);
    setIsLogoutModalOpen(true);
  };

  const handleCloseLogoutModal = () => {
    setIsLogoutModalOpen(false);
  };

  const handleConfirmLogout = () => {
    clearAuthSession();
    setIsLogoutModalOpen(false);
    redirectToLogin();
  };

  const sessionAvatar = resolveUserImage(session?.usuario);
  const avatarSrc = !hasAvatarError && sessionAvatar ? sessionAvatar : profileAvatar;
  const displayName = resolveUserDisplayName(session?.usuario);

  return (
    <TopbarProfileWrap>
      <TopbarAvatarButton type="button" onClick={toggleCollapse}>
        <TopbarAvatarImage
          src={avatarSrc}
          alt="avatar"
          onError={() => setHasAvatarError(true)}
        />
        <TopbarAvatarName>{displayName}</TopbarAvatarName>
        <TopbarDownIcon />
      </TopbarAvatarButton>
      {isCollapsed && (
        <TopbarBack
          type="button"
          aria-label="button collapse"
          onClick={toggleCollapse}
        />
      )}
      <Collapse in={isCollapsed}>
        <TopbarMenuWrap>
          <TopbarMenu>
            <TopbarMenuLink title="Perfil" icon="user" path="/pages/perfil" onClick={toggleCollapse} />
            <TopbarMenuLink title="Sair" icon="exit" path="/log_in" onClick={handleLogoutClick} />
          </TopbarMenu>
        </TopbarMenuWrap>
      </Collapse>
      <Modal centered show={isLogoutModalOpen} onHide={handleCloseLogoutModal}>
        <StyledModalHeader closeButton>
          <Modal.Title>
            <ModalTitleContent>
              <ModalTitleIcon className="lnr lnr-exit" />
              <span>Tem certeza que quer sair?</span>
            </ModalTitleContent>
          </Modal.Title>
        </StyledModalHeader>
        <StyledModalBody>
          Ao confirmar, sua sessão será encerrada.
        </StyledModalBody>
        <ModalFooterWrap>
          <Button variant="secondary" onClick={handleCloseLogoutModal}>
            <span>Cancelar</span>
          </Button>
          <Button variant="danger" onClick={handleConfirmLogout}>
            <span>Sair</span>
          </Button>
        </ModalFooterWrap>
      </Modal>
    </TopbarProfileWrap>
  );
};

export default TopbarProfile;

// region STYLES

export const TopbarProfileWrap = styled.div`
  position: relative;
  margin-bottom: 0;
  ${marginLeft}: 0;

  @media screen and (max-width: 576px) {
    margin: inherit;
  }

  @media screen and (max-width: 320px) {
    margin: auto 0;
  }
`;

const TopbarAvatarButton = styled.button`
  height: 100%;
  display: flex;
  cursor: pointer;
  position: relative;
  border-radius: 0;
  border: none;
  transition: all 0.3s;
  box-shadow: none;
  padding: 0 15px;
  background-color: transparent;

  &:hover, &:focus, &:active, &:focus:active {
    background-color: rgba(255, 255, 255, 0.08);
  }

  &:focus {
    outline: none;
  }

  &:before {
    display: none;
  }  
`;

const TopbarAvatarImage = styled.img`
  margin: auto 0;
  border-radius: 50%;
  height: 36px;
  width: 36px;
  border: 2px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.12);
`;

export const TopbarAvatarName = styled.p`
  margin: auto 0;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  display: none;
  ${marginLeft}: 10px;
  color: ${colorWhite};

  @media screen and (min-width: 480px) {
      display: block;
  }
`;

const TopbarMenuWrap = styled.div`
  z-index: 101;
  position: absolute;
  width: 100%;
  min-width: 210px;
  ${right}: 0;

  @media screen and (max-width: 320px) {
    ${right}: -50px;
  }
`;

const TopbarMenu = styled.div`
  width: 200px;
  border-radius: 16px;
  border: 1px solid ${colorBorder};
  padding: 15px 0;
  box-shadow: 0 18px 36px rgba(25, 36, 56, 0.16);
  margin-top: 10px;
  background: ${colorBackground};

  button {
    padding: 0;

    &:hover {
      background-color: ${colorHover};
    }

    &${TopbarLink} {
      background-color: transparent;
      border: none;
      padding: 9px 20px;
    }
  }

  *:focus {
    outline: none;
  }

  @media screen and (min-width: 480px) {
    width: 100%;
    ${left}: 0 !important;
  }
`;

const StyledModalHeader = styled(Modal.Header)`
  border-bottom: 1px solid ${colorBorder};

  .modal-title {
    color: ${colorText};
    font-size: 18px;
    font-weight: 600;
  }
`;

const ModalTitleContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const ModalTitleIcon = styled.i`
  color: ${colorText};
  font-size: 18px;
  line-height: 1;
`;

const StyledModalBody = styled(Modal.Body)`
  color: ${colorTextAdditional};
  font-size: 14px;
  line-height: 1.6;
`;

// endregion
